import type { StockRequestLine } from './stockRequestStore';

export type AssignmentStep = 'review' | 'order' | 'requester';

export function assignmentStepLabel(step: AssignmentStep | null | undefined): string {
  switch (step) {
    case 'review': return 'Review';
    case 'order': return 'Place order';
    case 'requester': return 'Your action';
    default: return '';
  }
}

export type SubstituteStatus =
  | 'pending_requester'
  | 'pending_manager'
  | 'accepted'
  | 'declined'
  | null;

export function hasPendingRequesterReview(lines: StockRequestLine[]): boolean {
  return lines.some(l => l.substituteStatus === 'pending_requester');
}

export function hasPendingManagerReview(lines: StockRequestLine[]): boolean {
  return lines.some(l => l.substituteStatus === 'pending_manager');
}

export function hasPendingNegotiation(lines: StockRequestLine[]): boolean {
  return hasPendingRequesterReview(lines) || hasPendingManagerReview(lines);
}

/** All negotiated changes are settled — safe to auto-approve without another review pass. */
export function canAutoApproveAfterNegotiation(lines: StockRequestLine[]): boolean {
  if (hasPendingNegotiation(lines)) return false;
  const active = lines.filter(l => l.status !== 'cancelled');
  if (active.length === 0) return false;
  return !active.some(l => l.status === 'pending_resolution' || l.status === 'disputed');
}

export function defaultManagerIds(
  settings: { defaultOrdererIds?: string[]; defaultApproverIds?: string[] } | null,
): string[] {
  if (!settings) return [];
  if (settings.defaultOrdererIds && settings.defaultOrdererIds.length > 0) {
    return settings.defaultOrdererIds;
  }
  return settings.defaultApproverIds ?? [];
}

/** @deprecated use hasPendingRequesterReview */
export const hasPendingSubstitutes = hasPendingRequesterReview;

export function isReviewAssignment(
  step: AssignmentStep | null | undefined,
  status: string,
): boolean {
  return step === 'review' || (step == null && status === 'submitted');
}

export function isOrderAssignment(
  step: AssignmentStep | null | undefined,
  status: string,
  lines: StockRequestLine[],
): boolean {
  if (step === 'order') return true;
  return step == null
    && (status === 'approved' || status === 'partially_ordered')
    && !hasPendingNegotiation(lines);
}

/** Request is still in the review / ordering workflow (not fully ordered). */
export function isInReviewQueue(
  status: string,
  assignmentStep: AssignmentStep | null | undefined,
): boolean {
  if (status === 'submitted') return true;
  if (status === 'approved') return assignmentStep !== 'requester';
  if (status === 'partially_ordered') return true;
  return false;
}

/** Request has at least some ordered lines and stock may be received. */
export function isInReceivingQueue(status: string): boolean {
  return status === 'partially_ordered'
    || status === 'ordered'
    || status === 'partially_received';
}

/** Active pipeline — submitted through partially received (excludes draft / completed / cancelled). */
export function isPendingRequestStatus(status: string): boolean {
  return status === 'submitted'
    || status === 'approved'
    || status === 'partially_ordered'
    || status === 'ordered'
    || status === 'partially_received';
}

export function countPendingRequests(
  requests: { status: string; warehouseId: string }[],
  warehouseId?: string | null,
): number {
  const list = warehouseId
    ? requests.filter(r => r.warehouseId === warehouseId)
    : requests;
  return list.filter(r => isPendingRequestStatus(r.status)).length;
}

export function pickAssignee(ids: string[], excludeId?: string | null): string | null {
  if (ids.length === 0) return null;
  const pool = excludeId ? ids.filter(id => id !== excludeId) : ids;
  return pool[0] ?? ids[0] ?? null;
}
